import { CALL_API2 } from "../Utils/const";
import { getAuthToken } from "../context/auth-context";

// Convierte solo el texto fuera de etiquetas HTML a mayúsculas
function toUpperCaseTextPreservingHtml(str) {
  if (typeof str !== 'string') return str;
  // Convierte a mayúsculas solo el texto fuera de etiquetas HTML
  let result = str.replace(/([^<>]+)(?=<|$)/g, (match) => match.toUpperCase());
  // Convierte &nbsp; a &NBSP;
  result = result.replace(/&NBSP;/gi, '&nbsp;');
  return result;
}

function toUpperCaseObject(obj) {
  if (typeof obj !== 'object' || obj === null) {
    if (typeof obj === 'string') {
      return toUpperCaseTextPreservingHtml(obj);
    }
    return obj;
  }
  if (Array.isArray(obj)) {
    return obj.map(toUpperCaseObject);
  }
  return Object.fromEntries(
    Object.entries(obj).map(([key, value]) => [
      key,
      toUpperCaseObject(value)
    ])
  );
}

export async function getAllPresupuestos() {
  const token = getAuthToken();
  const response = await fetch(`${CALL_API2}/presupuestos`, {
    headers: { Authorization: `Bearer ${token}` },
  });

  const data = await response.json();

  if (!response.ok) {
    throw new Error(data.message || 'Error fetching presupuestos');
  }

  return data.data;
}

export async function getPresupuestoById(idPresupuesto) {
  const token = getAuthToken();
  const response = await fetch(`${CALL_API2}/presupuestos/${idPresupuesto}`, {
    headers: { Authorization: `Bearer ${token}` },
  });

  const data = await response.json();

  if (!response.ok) {
    throw new Error(data.message || 'Error fetching presupuesto');
  }

  return data.data;
}

export function validatePresupuesto(presupuestoObject) {
  // Verificar campos obligatorios
  if (!presupuestoObject.itemsPresupuestos) {
    throw new Error('itemsPresupuestos es obligatorio');
  }
  if (!presupuestoObject.condiciones) {
    throw new Error('condiciones es obligatorio');
  }
  if (!presupuestoObject.nombre_cliente) {
    throw new Error('nombre_cliente es obligatorio');
  }
  if (!presupuestoObject.validohasta) {
    throw new Error('validohasta es obligatorio');
  }
  if (!presupuestoObject.fecha) {
    throw new Error('fecha es obligatorio');
  }

  // Validar total
  if (!Number.isInteger(presupuestoObject.total) || presupuestoObject.total <= 0) {
    throw new Error('total debe ser un entero positivo');
  }

  // Validar subtotal
  if (!Number.isInteger(presupuestoObject.subtotal) || presupuestoObject.subtotal <= 0) {
    throw new Error('subtotal debe ser un entero positivo');
  }

  // Validar descuento
  if (!Number.isInteger(presupuestoObject.descuento) || presupuestoObject.descuento < 0) {
    throw new Error('descuento debe ser un entero no negativo');
  }

  return true;
}

export async function insertPresupuesto(presupuestoObject) {
  // Convert all string values in presupuestoObject to uppercase (except HTML)
  const upperCasePresupuesto = toUpperCaseObject(presupuestoObject);

  // Validar presupuestoObject antes de hacer la llamada a la API
  validatePresupuesto(upperCasePresupuesto);

  const token = getAuthToken();
  const response = await fetch(`${CALL_API2}/presupuestos`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(upperCasePresupuesto),
  });

  const data = await response.json();
  if (!response.ok) {
    throw new Error(data.message || 'Error al guardar el presupuesto');
  }

  return data;
}

export async function updatePresupuesto(idPresupuesto, presupuestoObject) {
  const upperCasePresupuesto = toUpperCaseObject(presupuestoObject);
  const token = getAuthToken();
  const response = await fetch(`${CALL_API2}/presupuestos/${idPresupuesto}`, {
    method: "PUT",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(upperCasePresupuesto),
  });

  const data = await response.json();

  if (!response.ok) {
    throw new Error(data.message || 'Error updating presupuesto');
  }

  return data.isUpdated;
}

export async function updateItemPresupuesto(idPresupuesto, idItem, itemObject) {
  const token = getAuthToken();
  const response = await fetch(`${CALL_API2}/presupuestos/${idPresupuesto}/items/${idItem}`, {
    method: "PUT",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(itemObject),
  });

  const data = await response.json();

  if (!response.ok) {
    throw new Error(data.message || 'Error updating item presupuesto');
  }

  return data.isUpdated;
}

// ------- MODIFICADO: Aplica Uppercase a condiciones --------
export async function updateCondicionesPresupuesto(idPresupuesto, idCon, condiciones) {
  // Aplica la función de mayúsculas solo texto (dejando HTML)
  const upperCaseCondiciones = toUpperCaseObject(condiciones);

  const token = getAuthToken();
  const response = await fetch(`${CALL_API2}/presupuestos/${idPresupuesto}/condiciones/${idCon}`, {
    method: "PUT",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(upperCaseCondiciones),
  });

  const data = await response.json();

  if (!response.ok) {
    throw new Error(data.message || 'Error updating condiciones');
  }

  return data.isUpdated;
}

export async function updateEstadoPresupuesto(idPresupuesto, estado, motivoRechazo) {
  const token = getAuthToken();

  const estadoMayus = typeof estado === "string" ? estado.toUpperCase().trim() : estado;
  const isRechazado = String(estadoMayus) === "RECHAZADO";

  // Normaliza motivo: recorta y limita a 200 chars
  const motivoClean = isRechazado
    ? String(motivoRechazo ?? "").trim().slice(0, 200)
    : null;

  // Si es RECHAZADO, motivo es obligatorio
  if (isRechazado && !motivoClean) {
    throw new Error("Debe proporcionar 'motivorechazo' cuando el estado es RECHAZADO.");
  }

  const payload = {
    estado: estadoMayus,
    // En RECHAZADO enviamos el texto; en otros estados forzamos null
    motivorechazo: isRechazado ? motivoClean : null,
  };

  const response = await fetch(`${CALL_API2}/presupuestos/${idPresupuesto}/estado`, {
    method: "PUT",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(payload),
  });

  const data = await response.json();
  if (!response.ok) {
    throw new Error(data.message || "Error updating estado");
  }
  return data.isUpdated;
}

export async function insertItemPresupuesto(itemObject) {
  const token = getAuthToken();
  const response = await fetch(`${CALL_API2}/presupuestos/insertItem`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(itemObject),
  });

  const data = await response.json();

  if (!response.ok) {
    throw new Error(data.message || 'Error insertando ítem del presupuesto');
  }

  return data;
}

// ------- MODIFICADO: Aplica Uppercase a condicionesObject --------
export async function insertCondicionesPresupuesto(condicionesObject) {
  // Aplica la función de mayúsculas solo texto (dejando HTML)
  const upperCaseCondiciones = toUpperCaseObject(condicionesObject);

  const token = getAuthToken();
  const response = await fetch(`${CALL_API2}/presupuestos/insertCon`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(upperCaseCondiciones),
  });

  const data = await response.json();

  if (!response.ok) {
    throw new Error(data.message || 'Error insertando condiciones del presupuesto');
  }

  return data;
}
export async function deleteItemPresupuesto(idItem) {
  const token = getAuthToken();
  const response = await fetch(`${CALL_API2}/deleteitempresupuesto/${idItem}`, {
    method: "DELETE",
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  const data = await response.json();

  if (!response.ok) {
    throw new Error(data.message || 'Error eliminando ítem del presupuesto');
  }

  return data.isDeleted; // Asegúrate que tu backend retorne esta propiedad
}
export async function deleteConPresupuesto(idCondicion) {
  const token = getAuthToken();
  const response = await fetch(`${CALL_API2}/deleteconpresupuesto/${idCondicion}`, {
    method: "DELETE",
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  const data = await response.json();

  if (!response.ok) {
    throw new Error(data.message || 'Error eliminando condición del presupuesto');
  }

  return data.isDeleted; // Asegúrate que tu backend retorne esta propiedad
}